import TestNepaliDatePicker from './components/TestNepaliDatePicker';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProviderWrapper } from './contexts/ThemeContext';

// Components
import Login from './components/auth/Login';
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import UserManagement from './components/admin/UserManagement';
import ActivityLog from './components/admin/ActivityLog';

// Karigar Module
import KarigarList from './components/karigar/KarigarList';
import KarigarForm from './components/karigar/KarigarForm';
import DespatchForm from './components/karigar/DespatchForm';
import PendingIssues from './components/karigar/PendingIssues';
import ItemIssue from './components/karigar/ItemIssue';
import DirectOrder from './components/karigar/DirectOrder';
import KarigarOverview from './components/karigar/KarigarOverview';
import OrderList from './components/karigar/OrderList';
import DirectOrderList from './components/karigar/DirectOrderList';
import WorkReceipt from './components/karigar/WorkReceipt';
import MetalIssue from './components/karigar/MetalIssue';
import PendingList from './components/karigar/PendingList';
import MetalIssueList from './components/karigar/MetalIssueList';

// Shop Module
import OrderMain from './components/shop/OrderMain';
import ShopOrderList from './components/shop/OrderList';
import ShopDespatch from './components/shop/ShopDespatch';
import ShopPendingIssues from './components/shop/ShopPendingIssues';
import ShopItemIssue from './components/shop/ShopItemIssue';
import ShopDirectOrder from './components/shop/ShopDirectOrder';
import ShopDirectOrderList from './components/shop/ShopDirectOrderList';
import CustomerList from './components/shop/CustomerList';
import CustomerForm from './components/shop/CustomerForm';
import OrderForm from './components/shop/OrderForm';
import AddItemPage from './components/shop/AddItemPage';
import ShopWorkReceipt from './components/shop/ShopWorkReceipt';
import ShopPendingList from './components/shop/ShopPendingList';
import ShopOverview from './components/shop/ShopOverview';
import ShopKarigarList from './components/shop/ShopKarigarList';
import ShopKarigarForm from './components/shop/ShopKarigarForm';
import ShopMetalIssue from './components/shop/ShopMetalIssue';
import ShopKarigarOverview from './components/shop/ShopKarigarOverview';
import UpdateOrderForm from './components/shop/UpdateOrderForm';
import UpdateDirectOrder from './components/karigar/UpdateDirectOrder';
import ShopMetalIssueList from './components/shop/ShopMetalIssueList';

// Transaction Module
import Transaction22K from './components/transaction/Transaction22K';
import Transaction24K from './components/transaction/Transaction24K';
import TransactionOther1 from './components/transaction/TransactionOther1';
import TransactionOther2 from './components/transaction/TransactionOther2';
import TransactionTotalBalance from './components/transaction/TransactionTotalBalance';
import TransactionPreviousBalance from './components/transaction/TransactionPreviousBalance';
import KarigarTransaction from './components/karigar/KarigarTransaction';
import ShopKarigarTransaction from './components/shop/ShopKarigarTransaction';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], allowedSubadminTypes = [] }) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin can access everything
  if (user?.role === 0) {
    return children;
  }

  // Check subadmin permissions
  if (user?.role === 1) {
    if (allowedSubadminTypes.length === 0 || allowedSubadminTypes.includes(user.subadminType)) {
      return children;
    }
  }

  return <Navigate to="/dashboard" replace />;
};

// Main App Component
const AppContent = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Routes>
          <Route path="/karigar/transactions" element={
            <ProtectedRoute allowedSubadminTypes={["karigar","shop"]}>
              <Layout>
                <KarigarTransaction />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/shop/karigar-transactions" element={
            <ProtectedRoute allowedSubadminTypes={["shop","karigar"]}>
              <Layout>
                <ShopKarigarTransaction />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          } />
          
          <Route path="/" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={[0]}>
              <Layout>
                <UserManagement />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/activity-logs" element={
            <ProtectedRoute allowedRoles={[0]}>
              <Layout>
                <ActivityLog />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Karigar Module Routes */}
          <Route path="/karigar" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <KarigarList />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/add" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <KarigarForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/edit/:id" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <KarigarForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/despatch" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <DespatchForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/pending-issues" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <PendingIssues />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/item-issue" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <ItemIssue />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/direct-order" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <DirectOrder />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/karigar/karigar-overview" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <KarigarOverview />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/karigar/orders" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <OrderList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/karigar/direct-orders" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <DirectOrderList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/karigar/work-receipt" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <WorkReceipt />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/karigar/metal-issue" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <MetalIssue />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/karigar/pending-list" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <PendingList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/karigar/metal-issue-list" element={
            <ProtectedRoute allowedSubadminTypes={['karigar']}>
              <Layout>
                <MetalIssueList />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Shop Module Routes */}
          <Route path="/shop" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopKarigarList />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/add" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopKarigarForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/edit/:id" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopKarigarForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/orders" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopOrderList />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/order-main" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <OrderMain />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/despatch" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopDespatch />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/pending-issues" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopPendingIssues />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/item-issue" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopItemIssue />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/direct-order" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopDirectOrder />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/direct-orders" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopDirectOrderList />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/customers" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <CustomerList />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/customers/add" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <CustomerForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/shop/customers/edit/:id" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <CustomerForm />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/shop/orders/add" element={
            <ProtectedRoute>
              <Layout>
                <OrderForm />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/shop/items/add" element={
            <ProtectedRoute>
              <Layout>
                <AddItemPage />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/shop/work-receipt" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopWorkReceipt />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/shop/pending-list" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopPendingList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/shop/shop-karigar-overview" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopKarigarOverview />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/shop/metal-issue" element={
            <ProtectedRoute allowedSubadminTypes={['shop']}>
              <Layout>
                <ShopMetalIssue />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/update-order" element={
            <ProtectedRoute>
              <Layout>
                <UpdateOrderForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/update-direct-order" element={
            <ProtectedRoute>
              <Layout>
                <UpdateDirectOrder />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Transaction Module Routes */}
          <Route path="/transaction/22k" element={
            <ProtectedRoute allowedSubadminTypes={['transaction']}>
              <Layout>
                <Transaction22K />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/transaction/24k" element={
            <ProtectedRoute allowedSubadminTypes={['transaction']}>
              <Layout>
                <Transaction24K />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/transaction/other1" element={
            <ProtectedRoute allowedSubadminTypes={['transaction']}>
              <Layout>
                <TransactionOther1 />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/transaction/other2" element={
            <ProtectedRoute allowedSubadminTypes={['transaction']}>
              <Layout>
                <TransactionOther2 />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/transaction/total-balance" element={
            <ProtectedRoute allowedSubadminTypes={['transaction']}>
              <Layout>
                <TransactionTotalBalance />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/transaction/previous-balance" element={
            <ProtectedRoute allowedSubadminTypes={['transaction']}>
              <Layout>
                <TransactionPreviousBalance />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Nepali Date Picker Test Route */}
          <Route path="/test-nepali-date-picker" element={<TestNepaliDatePicker />} />

          {/* Catch all route */}
          <Route path="*" element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
          } />
        </Routes>
      </Box>
    </Router>
  );
};



// Query Client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProviderWrapper>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProviderWrapper>
    </QueryClientProvider>
  );
}

export default App;
